/**
 * Check if a value is null, undefined, or an empty string
 *
 * Useful for validating user input, API responses, or optional fields where empty strings
 * should be treated the same as null/undefined.
 *
 * @param {T} value - Value to check (any type)
 *
 * @return {boolean} True if value is null, undefined, or empty string; false otherwise
 *
 * @example
 * isNullUndefinedOrEmpty(null);        // true
 * isNullUndefinedOrEmpty(undefined);   // true
 * isNullUndefinedOrEmpty('');          // true
 * isNullUndefinedOrEmpty('hello');     // false
 * isNullUndefinedOrEmpty(0);           // false
 * isNullUndefinedOrEmpty(false);       // false
 */
export const isNullUndefinedOrEmpty = <T>(value: T): boolean => value === null || value === undefined || value === '';

/**
 * Check if an array is null, undefined, or empty (length 0)
 *
 * @param {T[] | null | undefined} value - Array to check
 *
 * @return {boolean} True if value is nullish, not an array, or has length 0
 *
 * @example
 * isEmptyArray(null);              // true
 * isEmptyArray(undefined);         // true
 * isEmptyArray([]);                // true
 * isEmptyArray([1, 2, 3]);         // false
 * isEmptyArray('not an array');    // true (type guard)
 */
export const isEmptyArray = <T>(value: T[] | null | undefined): boolean =>
  !value || !Array.isArray(value) || value.length === 0;

/**
 * Check if an object is null, undefined, or empty (no own properties)
 *
 * Excludes arrays from consideration - they are treated as not-objects.
 *
 * @param {object | null | undefined} value - Object to check
 *
 * @return {boolean} True if value is nullish, an array, or has no own properties
 *
 * @example
 * isEmptyObject(null);             // true
 * isEmptyObject(undefined);        // true
 * isEmptyObject({});               // true
 * isEmptyObject({ key: 'value' }); // false
 * isEmptyObject([]);               // true (empty arrays)
 * isEmptyObject([1, 2]);           // false (arrays not excluded)
 */
export const isEmptyObject = (value: object | null | undefined): boolean =>
  !value ||
  (Array.isArray(value) ? value.length === 0 : value.constructor === Object && Object.keys(value).length === 0);
